import React, { useState } from 'react';
import {
  ChatChoice,
  ChatChoiceOption,
  ChatChoiceSelectedOption,
  resolveChoiceLocalizedText
} from '../types';

interface MessageChoicesProps {
  choices: ChatChoice[];
  disabled?: boolean;
  onSubmit: (choiceId: number, selected: ChatChoiceSelectedOption[]) => void;
}

const sortByOrder = <T extends { order: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.order - b.order);

const toSelectedOption = (option: ChatChoiceOption): ChatChoiceSelectedOption => ({
  optionId: option.id,
  label: resolveChoiceLocalizedText(option.label),
  value: option.value
});

const MessageChoices: React.FC<MessageChoicesProps> = ({ choices, disabled, onSubmit }) => {
  const orderedChoices = sortByOrder(choices);
  const [multiSelections, setMultiSelections] = useState<Record<number, ChatChoiceSelectedOption[]>>(
    {}
  );

  if (orderedChoices.length === 0) {
    return null;
  }

  const toggleMultiOption = (choice: ChatChoice, option: ChatChoiceOption) => {
    setMultiSelections(prev => {
      const current = prev[choice.id] ?? [];
      const exists = current.some(item => item.value === option.value);
      const next = exists
        ? current.filter(item => item.value !== option.value)
        : [...current, toSelectedOption(option)];
      return { ...prev, [choice.id]: next };
    });
  };

  const isMultiSelected = (choiceId: number, value: string) =>
    (multiSelections[choiceId] ?? []).some(item => item.value === value);

  return (
    <div className="chatyme-message-choices">
      {orderedChoices.map(choice => {
        const options = sortByOrder(choice.options ?? []);
        const isMulti = choice.selectionMode === 'multi';
        const selected = multiSelections[choice.id] ?? [];
        const title = resolveChoiceLocalizedText(choice.title);

        return (
          <div key={choice.id} className="chatyme-message-choice">
            {title ? <div className="chatyme-message-choice-title">{title}</div> : null}
            <div className="chatyme-message-choice-options">
              {options.map(option => {
                const label = resolveChoiceLocalizedText(option.label);
                const selectedClass =
                  isMulti && isMultiSelected(choice.id, option.value)
                    ? ' chatyme-message-choice-option-selected'
                    : '';

                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`chatyme-message-choice-option${selectedClass}`}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) {
                        return;
                      }
                      if (isMulti) {
                        toggleMultiOption(choice, option);
                        return;
                      }
                      onSubmit(choice.id, [toSelectedOption(option)]);
                    }}
                  >
                    <span className="chatyme-message-choice-option-label">{label}</span>
                    <span className="material-icons chatyme-message-choice-option-icon" aria-hidden>
                      {isMulti ? (isMultiSelected(choice.id, option.value) ? 'check_circle' : 'radio_button_unchecked') : 'chevron_right'}
                    </span>
                  </button>
                );
              })}
            </div>
            {isMulti ? (
              <button
                type="button"
                className="chatyme-message-choice-confirm"
                disabled={disabled || selected.length === 0}
                onClick={() => {
                  if (disabled || selected.length === 0) {
                    return;
                  }
                  onSubmit(choice.id, selected);
                }}
              >
                Confirm
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default MessageChoices;
